import type { MouseEventHandler, MouseEvent as ReactMouseEvent, ReactElement } from "react";
import { Children, cloneElement, useCallback, useEffect, useRef } from "react";

/**
 * useClickOutside custom hook
 * This hook registers mouseDown capture and mouseUp event and returns onMouseDownCapture and onMouseDown handlers
 * to be used in the component. Or, you may use ClickOutsideWrapper on a component.
 *
 * Guideline: By default, click down+up or down only or up only in the popup element, will prevent from the popup
 * to be closed.
 *
 *   1. mouseDown(Capture) [document] - true (init)
 *   2. mouseDown(Capture) [wrapper] - false
 *   3. mouseup [wrapper] - false
 *   4. mouseup [document] - no change, if outside - invoke callback
 *
 * @param callback - the callback to call when clicked outside
 * @param checkIsClickOutside - function that determines whether the event is inside/outside of the element.
 */

type ICheckIsOutside = (isOutside: boolean, prevState: boolean | undefined, evt: MouseEvent) => boolean;

const checkOutsideMechanism: { [key: string]: ICheckIsOutside } = {
    // outside only if both down & up are outside
    fullOutside: (isOutside) => isOutside,

    // outside if any down or up is outside
    partialOutside: ((): ICheckIsOutside => {
        let countInside: number;
        return (isOutside, prevState) => {
            if (prevState === undefined) {
                countInside = 0;
            } else if (!isOutside) {
                countInside += 1;
            }
            return countInside < 2;
        };
    })(),
};

const useClickOutside = (
    callback: () => void,
    checkIsOutside: ICheckIsOutside = checkOutsideMechanism.fullOutside,
): { onMouseDownCapture: MouseEventHandler; onMouseUp: MouseEventHandler } => {
    const isOutsideRef = useRef<boolean | undefined>(undefined);

    //effect to detect click in the DOM
    useEffect(() => {
        const handleDocumentMouseDownCapture = (evt: MouseEvent) => {
            isOutsideRef.current = checkIsOutside(true, isOutsideRef.current, evt); // init flag
        };
        const handleDocumentMouseUp = () => {
            if (isOutsideRef.current) {
                callback();
            }
            isOutsideRef.current = undefined;
        };

        document.addEventListener("mousedown", handleDocumentMouseDownCapture, { capture: true });
        document.addEventListener("mouseup", handleDocumentMouseUp, { capture: true });
        return () => {
            document.removeEventListener("mousedown", handleDocumentMouseDownCapture, {
                capture: true,
            });
            document.removeEventListener("mouseup", handleDocumentMouseUp, { capture: true });
        };
    }, [isOutsideRef, callback]);

    // events to track local clicks - onCurrentTargetCapture
    const onMouseDownCapture = useCallback((evt: ReactMouseEvent): void => {
        isOutsideRef.current = checkIsOutside(false, isOutsideRef.current, evt.nativeEvent);
    }, []);

    const onMouseUp = useCallback((evt: ReactMouseEvent): void => {
        isOutsideRef.current = checkIsOutside(false, isOutsideRef.current, evt.nativeEvent);
    }, []);

    return { onMouseDownCapture, onMouseUp };
};

interface IClickOutsideWrapperProps {
    children: ReactElement<FixmeType>;
    onClickOutside: () => void;
    noWrap?: boolean;
    checkIsOutside?: ICheckIsOutside;
}

export const ClickOutsideWrapper = ({
    children,
    onClickOutside,
    noWrap = false,
    checkIsOutside,
}: IClickOutsideWrapperProps) => {
    const { onMouseDownCapture, onMouseUp } = useClickOutside(onClickOutside, checkIsOutside);

    if (noWrap) {
        const { onMouseDownCapture: origOnMouseDownCapture, onMouseUp: origOnMouseUp } = Children.only(children).props;
        const onMouseDownCaptureWrapped = useCallback(
            (evt: ReactMouseEvent, ...restArgs: FixmeType[]) => {
                onMouseDownCapture(evt);
                if (origOnMouseDownCapture) {
                    origOnMouseDownCapture(evt, ...restArgs);
                }
            },
            [onMouseDownCapture, origOnMouseDownCapture],
        );
        const onMouseUpWrapped = useCallback(
            (evt: ReactMouseEvent, ...restArgs: FixmeType[]) => {
                if (origOnMouseUp) {
                    origOnMouseUp(evt, ...restArgs);
                }
                onMouseUp(evt);
            },
            [onMouseUp, origOnMouseUp],
        );
        return cloneElement(Children.only(children), {
            onMouseDownCapture: onMouseDownCaptureWrapped,
            onMouseUp: onMouseUpWrapped,
        });
    }

    return (
        <span
            onMouseDownCapture={onMouseDownCapture}
            onMouseUp={onMouseUp}
        >
            {children}
        </span>
    );
};
